import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import './Navbar.css'
import { AuthNavActions, useStructuraAuth } from '@structura/auth'
import { useTranslation, LanguageSwitcher } from '@structura/i18n'

export interface NavbarProps {
  // Path of the beam-designer page for this app (e.g. "/beam/wood"). The
  // navbar's "Beam Designer" link uses this; signed-out visitors see it
  // disabled with a lock icon and a sign-in prompt.
  beamRoute: string

  // Label shown for the beam-designer link (e.g. "Wood Beam Designer").
  beamLabel: string
}

const Navbar: React.FC<NavbarProps> = ({ beamRoute, beamLabel }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const authState = useStructuraAuth()
  const { t } = useTranslation()

  const isActive = (path: string): boolean => location.pathname === path

  // Handler for disabled nav items — shows tooltip and navigates to login.
  const handleDisabledClick = (event: React.MouseEvent): void => {
    event.preventDefault()
    navigate('/account', { state: { message: t('nav.signInPrompt') } })
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-dark navbar-soft">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img src="/logo.svg" alt="StructuraMatrix Logo" className="navbar-logo" />
          <span className="brand-text">Structura<span className="brand-highlight">Matrix</span></span>
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label={t('nav.toggle')}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto align-items-lg-center">
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/') ? 'active' : ''}`}
                to="/"
              >
                <i className="bi bi-house me-1"></i>
                {t('nav.home')}
              </Link>
            </li>
            {/* Dashboard is the post-login landing but intentionally has no
                navbar entry — the [Home] link renders the Dashboard for
                authenticated users (see HomePage in @structura/pages). */}
            {/* Beam Designer — visible to all, accessible only when logged in. */}
            {authState.isAuthenticated ? (
              <li className="nav-item">
                <Link
                  className={`nav-link ${location.pathname.startsWith('/beam') ? 'active' : ''}`}
                  to={beamRoute}
                >
                  <i className="bi bi-hammer me-1"></i>
                  {beamLabel}
                </Link>
              </li>
            ) : (
              <li className="nav-item">
                <span
                  className="nav-link nav-link-disabled"
                  onClick={handleDisabledClick}
                  title={t('nav.beamDesigner.locked')}
                  role="button"
                  tabIndex={0}
                >
                  <i className="bi bi-hammer me-1"></i>
                  {beamLabel}
                  <i className="bi bi-lock-fill ms-1 nav-lock-icon"></i>
                </span>
              </li>
            )}
            <li className="nav-item">
              <Link
                className={`nav-link ${location.pathname.startsWith('/structura-bits') ? 'active' : ''}`}
                to="/structura-bits"
              >
                <i className="bi bi-stars me-1"></i>
                Structura-Bits
              </Link>
            </li>
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/about') ? 'active' : ''}`}
                to="/about"
              >
                <i className="bi bi-info-circle me-1"></i>
                {t('nav.about')}
              </Link>
            </li>
            {/* Admin is hidden from the navbar — access via direct URL /admin. */}
            <AuthNavActions beamRoute={beamRoute} />
            <li className="nav-item d-flex align-items-center">
              <LanguageSwitcher />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
